/**
 * External dependencies
 */
import isEmpty from 'lodash/isEmpty';

/**
 * DWT dependencies
 */
import Placeholder from '../block-components/Placeholder';

/**
 * WP dependencies
 */
import apiFetch from '@wordpress/api-fetch';

/**
 * General utilities
 */

import { removeHtmlTags } from '../utils/general-utils';

import {
  InspectorControls,
  __experimentalPanelColorGradientSettings as PanelColorGradientSettings,
  MediaUpload,
  MediaUploadCheck,
  RichText,
  useBlockProps,
  useInnerBlocksProps
} from '@wordpress/block-editor';

import {
  Button,
  PanelBody,
  SelectControl,
  TextControl,
  ToggleControl
} from '@wordpress/components';

import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 *
 * This component handles the block editor interface for the Featured Book block,
 * including API integration, media uploads, and all customization controls.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @param {Object} props
 * @param {Object} props.attributes
 * @param {Function} props.setAttributes
 * @param {string} props.clientId 
 * @return {WPElement} 
 */
const Edit = ({ attributes, setAttributes, clientId }) => {
  const {
    isbn,                
    apiImage,            
    uploadedImage,       
    bookTitle,           
    contributors,        
    description,
    format, 
    division,
    imprint,
    categories,
    backgroundStyle,     
    backgroundColor,     
    titleColor,          
    contributorsColor,   
    descriptionColor,    
    linkColor,           
    showTitle,           
    showContributors,    
    showDescription,     
    showReadMore,        
    showCTA,             
    workId,             
    altTextManual,
    uniqueId,     
  } = attributes;

  // Component state management
  /** @const {Object} data - State for storing API response data */
  /** @const {Function} setData - Function to update API data state */
  const [ data, setData ] = useState({});
  
  /** @const {boolean} isError - State for tracking API fetch errors */
  /** @const {Function} setError - Function to update error state */
  const [ isError, setError ] = useState( false );

  // Image and placeholder logic
  /** @const {boolean} isEmptyImage - Check if uploaded image is empty */
  const isEmptyImage = isEmpty( uploadedImage );
  
  /** @const {boolean} showPlaceholder - Determine if placeholder should be shown */
  const showPlaceholder = !!( isEmptyImage && !apiImage );

  // Block wrapper configuration
  /** @const {Object} blockProps - Block editor wrapper props */
  const blockProps = useBlockProps({
    className: `block__featured-book ${backgroundStyle === 'full-width' ? 'is-full-width' : ''}`
  });

  // Inner blocks configuration for CTA button
  /** @const {Array} template - Template for inner blocks (CTA button) */
  const template = [
    [ 'dwt/button', {} ]
  ];

  /** @const {Object} innerBlocksProps - Props and children for inner blocks */
  const { children, ...innerBlocksProps } = useInnerBlocksProps(
    { className: 'featured-book__cta' },
    {
      allowedBlocks: [ 'dwt/button' ],
      template,
      templateLock: 'all',
      renderAppender: showCTA ? undefined : false
    }
  );

  /**
   * API fetch logic
   */
  
  /**
   * Handle ISBN input changes
   * @param {string} newIsbn 
   */
  const onChangeIsbn = ( newIsbn ) => setAttributes({ isbn: newIsbn });

  /**
   * Fetch book data using ISBN
   * 
   */
  const onFetchApiData = async() => {
    await apiFetch({
      path: '/dwt/v2/single-title',
      method: 'POST',
      data: { isbn }
    })
      .then( ( response ) => {
        if ( isError ) {
          setError( false );
        }
        setData( response.data );
      })
      .catch( ( error ) => {
        console.error( error );
        setError( true );
      });
  };

  /**
   * Remove API-fetched data and reset related attributes
   * 
   */
  const onRemoveApiData = () => {
    setAttributes({
      apiImage: undefined,
      uploadedImage: undefined,
      bookTitle: '',
      contributors: '',
      description: '',
      format: '',
      division: '',
      imprint: '',
      categories: '',
      isbn: undefined,
      workId: undefined
    });
    setData({});
  };

  const getFormat = ( bookData ) => {
    let bookFormat;

    for ( const [ key, value ] of Object.entries( bookData.formats ) ) {
      const isbns = Object.keys( value );
      if ( isbns.includes( isbn ) ) {
        bookFormat = key;
      }
    }

    return bookFormat;
  };

  /**
   * Effect hook to process API data
   * Extracts and sets book information from API response
   */
  useEffect( () => {
    if ( isEmpty( data ) ) {
      return;
    }

    const encodedTitle = data.title || '';
    const encodedAuthor = data.frontlistiestTitle?.author || '';
    const encodedDescription = data.frontlistiestTitle?.aboutTheBook || '';

    setAttributes({
      bookTitle: encodedTitle,
      contributors: encodedAuthor,
      description: encodedDescription,
      format: getFormat( data ),
      division: data.frontlistiestTitle?.division.description,
      imprint: data.frontlistiestTitle?.imprint.name,
      categories: data.frontlistiestTitle?.categories || '',
      workId: data.workId,
      apiImage: isbn ? `https://images.penguinrandomhouse.com/cover/${isbn}` : undefined,
      altTextManual: `${encodedTitle} by ${encodedAuthor}`.trim()
    });
  }, [ data ] );

  /**
   * Set unique id for accessible aria-controls
   */
  useEffect( () => {
    if ( !attributes.uniqueId || attributes.uniqueId !== clientId ) {
      setAttributes({ uniqueId: clientId });
    }
  }, [ clientId ] );

  /**
   * Image settings section
   */
  
  /** @const {Array} ALLOWED_MEDIA_TYPES - Allowed file types for media upload */
  const ALLOWED_MEDIA_TYPES = [ 'image' ];
  
  /**
   * Handle uploaded image selection from media library
   * @param {Object} newUploadedImage - The selected image object
   */
  const onSelectUploadedImage = ( newUploadedImage ) => setAttributes({ uploadedImage: newUploadedImage });

  /**
   * Remove uploaded image and revert to API image
   */
  const onRemoveUploadedImage = () => {
    setAttributes({
      uploadedImage: undefined
    });
  };

  /** @const {string} uploadText */
  const uploadText = !isEmptyImage ? 'Replace image' : 'Upload image';
  
  /**
   * Handle manual alt text changes
   * @param {string} newAltTextManual 
   */
  const onChangeAltTextManual = ( newAltTextManual ) => setAttributes({ altTextManual: newAltTextManual });

  /**
   * Content handlers section
   */
  
  /**
   * Handle book title changes
   * @param {Array} newTitle - The new title as RichText array
   */
  const onChangeTitle = ( newTitle ) => setAttributes({ bookTitle: newTitle });
  
  /**
   * Handle contributors changes
   * @param {Array} newContributors - The new contributors as RichText array
   */
  const onChangeContributors = ( newContributors ) => setAttributes({ contributors: newContributors });
  
  /**
   * Handle description changes
   * @param {Array} newDescription - The new description as RichText array
   */
  const onChangeDescription = ( newDescription ) => setAttributes({ description: newDescription });

  /**
   * Render logic section
   */
  
  /** @const {string} imageSrc */
  const imageSrc = !isEmptyImage ? uploadedImage.sizes.full.url : apiImage;
  
  /** @const {string} imageAltText */
  const imageAltText = altTextManual || ( !isEmptyImage ? uploadedImage.alt : `${removeHtmlTags( bookTitle ) } by ${removeHtmlTags( contributors )}` );
  
  /** @const {string} inValidHelpText */
  const inValidHelpText = isError ? 'Please enter a valid ISBN' : 'Required';

  // Inline style objects for color customization
  /** @const {Object|undefined} containerStyle */
  const containerStyle = backgroundColor ? { backgroundColor } : undefined;
  
  /** @const {Object|undefined} titleStyle */
  const titleStyle = titleColor ? { color: titleColor } : undefined;
  
  /** @const {Object|undefined} contributorsStyle */
  const contributorsStyle = contributorsColor ? { color: contributorsColor } : undefined;
  
  /** @const {Object|undefined} descriptionStyle */
  const descriptionStyle = descriptionColor ? { color: descriptionColor } : undefined;

  return (
    <>
      {/* Block inspector controls (sidebar panel) */}
      <InspectorControls>
        {/* Book Settings Panel */}
        <PanelBody title={__( 'Book Settings', 'dwt-custom-theme' )}>
          {/* ISBN input and fetch button (shown when no API data) */}
          {!apiImage && (
            <>
              <TextControl
                className={isError ? 'has-error' : ''}
                label={__( 'ISBN', 'dwt-custom-theme' )}
                value={isbn}
                onChange={onChangeIsbn}
                help={__( inValidHelpText, 'dwt-custom-theme' )}
              />
              <Button
                onClick={onFetchApiData}
                className='editor-media-placeholder__button is-button is-default is-large is-secondary rounded is-button-fetch'
              >
                {__( 'Fetch book', 'dwt-custom-theme' )}
              </Button>
            </>
          )}

          {/* Book information and image controls (shown when API data exists) */}
          {apiImage && (
            <>
              {/* Display current book info */}
              <div className="featured-book__isbn">
                <h2>{bookTitle.length > 0 ? removeHtmlTags( bookTitle ) : 'Book Title'}</h2>
                <h2>ISBN</h2>
                <p className="mb-5">{isbn}</p>
                <Button
                  onClick={onRemoveApiData}
                  isLink
                  isDestructive
                >
                  {__( 'Remove ISBN', 'dwt-custom-theme' )}
                </Button>
              </div>

              {/* Image upload controls */}
              <div className="components-base-control">
                <MediaUploadCheck>
                  <MediaUpload
                    allowedTypes={ALLOWED_MEDIA_TYPES}
                    onSelect={onSelectUploadedImage}
                    type='image'
                    value={uploadedImage}
                    render={({ open }) => (
                      <Button
                        onClick={open}
                        icon='upload'
                        className='editor-media-placeholder__button is-button is-default is-large is-secondary is-upload-button'
                      >
                        {__( uploadText, 'dwt-custom-theme' )}
                      </Button>
                    )}
                  />
                </MediaUploadCheck>
                {!isEmptyImage && (
                  <>
                    <Button
                      onClick={onRemoveUploadedImage}
                      isLink
                      isDestructive
                      className='is-remove-button'
                    >
                      {__( 'Remove uploaded image', 'dwt-custom-theme' )}
                    </Button>
                    <p className="text-12 text-gray">Removing uploaded image defaults back to image from the API</p>
                  </>
                )}
              </div>
              
              {/* Alt text control */}
              <TextControl
                label={__( 'Alt Text', 'dwt-custom-theme' )}
                value={altTextManual}
                onChange={onChangeAltTextManual}
                help={__( 'Add alt text', 'dwt-custom-theme' )}
              />
            </>
          )}
        </PanelBody>

        {/* Module Settings Panel */}
        <PanelBody title={__( 'Module Settings', 'dwt-custom-theme' )}>
          <SelectControl
            label={__( 'Background Style', 'dwt-custom-theme' )}
            value={backgroundStyle}
            options={[
              { label: 'Default', value: 'default' },
              { label: 'Full Width', value: 'full-width' }
            ]}
            onChange={( newBackgroundStyle ) => setAttributes({ backgroundStyle: newBackgroundStyle })}
            __nextHasNoMarginBottom
          />
        </PanelBody>

        {/* Color Settings Panel */}
        <PanelColorGradientSettings
          title={__( 'Color Settings', 'dwt-custom-theme' )}
          settings={[
            {
              colorValue: backgroundColor,
              label: __( 'Background', 'dwt-custom-theme' ),
              onColorChange: ( newBackgroundColor ) => setAttributes({ backgroundColor: newBackgroundColor }),
              clearable: true
            },
            {
              colorValue: titleColor,
              label: __( 'Title', 'dwt-custom-theme' ),
              onColorChange: ( newTitleColor ) => setAttributes({ titleColor: newTitleColor }),
              clearable: true
            },
            {
              colorValue: contributorsColor,
              label: __( 'Contributors', 'dwt-custom-theme' ),
              onColorChange: ( newContributorsColor ) => setAttributes({ contributorsColor: newContributorsColor }),
              clearable: true
            },
            {
              colorValue: descriptionColor,
              label: __( 'Description', 'dwt-custom-theme' ),
              onColorChange: ( newDescriptionColor ) => setAttributes({ descriptionColor: newDescriptionColor }),
              clearable: true
            },
            {
              colorValue: linkColor,
              label: __( 'Link', 'dwt-custom-theme' ),
              onColorChange: ( newLinkColor ) => setAttributes({ linkColor: newLinkColor }),
              clearable: true
            }
          ]}
        />

        {/* Content Options Panel */}
        <PanelBody title={__( 'Content Options', 'dwt-custom-theme' )}>
          <ToggleControl
            label={__( 'Show Title', 'dwt-custom-theme' )}
            checked={showTitle}
            onChange={() => setAttributes({ showTitle: !showTitle })}
          />
          <ToggleControl
            label={__( 'Show Contributors', 'dwt-custom-theme' )}
            checked={showContributors}
            onChange={() => setAttributes({ showContributors: !showContributors })}
          />
          <ToggleControl
            label={__( 'Show Description', 'dwt-custom-theme' )}
            checked={showDescription}
            onChange={() => setAttributes({ showDescription: !showDescription })}
          />
          <ToggleControl
            label={__( 'Show Read More', 'dwt-custom-theme' )}
            checked={showReadMore}
            onChange={() => setAttributes({ showReadMore: !showReadMore })}
          />
          <ToggleControl
            label={__( 'Show CTA Button', 'dwt-custom-theme' )}
            checked={showCTA}
            onChange={() => setAttributes({ showCTA: !showCTA })}
          />
        </PanelBody>
      </InspectorControls>

      {/* Block content in editor */}
      <div {...blockProps} style={containerStyle}>
        <div className="featured-book--inner">
          <div className="featured-book__content-wrapper">
            {/* Book cover section */}
            <div className="featured-book__cover">
              {/* Show placeholder when no image is available */}
              {showPlaceholder && (
                <Placeholder
                  title={__( 'Book Cover', 'dwt-custom-theme' )}
                  text={__( 'Enter an ISBN from the side panel.', 'dwt-custom-theme' )}
                  icon='format-image'
                />
              )}

              {/* Show image with overlay when available */}
              {!showPlaceholder && (
                <div className="image-wrapper">
                  <div className="api-image-wrapper featured-book with-hover">
                    <img 
                      className="api-image" 
                      src={imageSrc} 
                      alt={imageAltText} 
                      data-isbn={isbn} 
                      data-workid={workId} 
                      loading="lazy" 
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Text content section */}
            <div className="featured-book__text-content">
              {/* Editable title */}
              {showTitle && (
                <RichText
                  className="featured-book__title heading-3"
                  tagName="h3"
                  placeholder={__( 'Book Title', 'dwt-custom-theme' )}
                  allowedFormats={[ 'core/bold', 'core/italic' ]}
                  value={bookTitle}
                  onChange={onChangeTitle}
                  style={titleStyle}
                />
              )}

              {/* Editable contributors */}
              {showContributors && (
                <RichText
                  className="featured-book__contributors paragraph-1 text-medium"
                  tagName="p"
                  placeholder={__( 'Contributors', 'dwt-custom-theme' )}
                  allowedFormats={[ 'core/bold', 'core/italic' ]}
                  value={contributors}
                  onChange={onChangeContributors}
                  style={contributorsStyle}
                />
              )}

              {/* Editable description with read more button */}
              {showDescription && (
                <div className="featured-book__description-wrapper">
                  <RichText
                    className="featured-book__description paragraph-1 text-medium"
                    tagName="div"
                    placeholder={__( 'Book Description', 'dwt-custom-theme' )}
                    allowedFormats={[ 'core/bold', 'core/italic', 'core/link' ]}
                    value={description}
                    onChange={onChangeDescription}
                    style={descriptionStyle}
                  />
                  {showReadMore && description.length > 480 && (
                    <button 
                      className="featured-book__read-more"
                      style={linkColor ? { color: linkColor } : {}}
                    >
                      {__( 'Read More >', 'dwt-custom-theme' )}
                    </button>
                  )}
                </div>
              )}
            </div>

            {showCTA && (
              <div className="featured-book__cta" {...innerBlocksProps}>
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Edit;
